/**
 * Google Analytics 4 埋点工具
 * 用于追踪用户行为和网站互动
 */

// 声明 gtag 全局函数类型
declare global {
  interface Window {
    gtag?: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer?: any[];
  }
}

/**
 * 发送自定义事件到 GA4
 * @param eventName 事件名称（使用中文，便于理解）
 * @param eventParams 事件参数
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, any>
) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', eventName, eventParams);
    console.log('📊 GA4事件:', eventName, eventParams);
  }
};

/**
 * 追踪页面浏览
 * @param pagePath 页面路径
 * @param pageTitle 页面标题
 */
export const trackPageView = (pagePath: string, pageTitle: string) => {
  trackEvent('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
  });
};

/**
 * 核心转化事件 - 联系相关
 */
export const analytics = {
  // 联系方式点击
  contact: {
    clickPhone: (phoneNumber: string) => {
      trackEvent('点击电话', {
        contact_method: '电话',
        phone_number: phoneNumber,
      });
    },
    clickEmail: (email: string) => {
      trackEvent('点击邮箱', {
        contact_method: '邮箱',
        email: email,
      });
    },
    viewWechat: () => {
      trackEvent('查看微信', {
        contact_method: '微信',
      });
    },
    viewContactPage: () => {
      trackEvent('访问联系页面', {
        page_type: '联系我们',
      });
    },
  },

  // PDF下载
  downloadPDF: (articleTitle: string, pdfUrl: string) => {
    trackEvent('下载PDF', {
      content_type: '技术文章',
      article_title: articleTitle,
      file_name: pdfUrl.split('/').pop(),
    });
  },

  // 产品相关
  product: {
    view: (productName: string, productCategory?: string) => {
      trackEvent('查看产品', {
        item_name: productName,
        item_category: productCategory || '未分类',
      });
    },
    clickCard: (productName: string) => {
      trackEvent('点击产品卡片', {
        item_name: productName,
      });
    },
    filterCategory: (category: string) => {
      trackEvent('筛选产品分类', {
        filter_category: category,
      });
    },
  },

  // 器件材料相关
  material: {
    view: (materialName: string, materialCategory?: string) => {
      trackEvent('查看器件', {
        item_name: materialName,
        item_category: materialCategory || '未分类',
      });
    },
    clickCard: (materialName: string) => {
      trackEvent('点击器件卡片', {
        item_name: materialName,
      });
    },
    filterCategory: (category: string) => {
      trackEvent('筛选器件分类', {
        filter_category: category,
      });
    },
  },

  // 文章相关
  article: {
    view: (articleTitle: string, articleCategory?: string) => {
      trackEvent('查看文章', {
        content_title: articleTitle,
        content_category: articleCategory || '未分类',
      });
    },
    clickCard: (articleTitle: string) => {
      trackEvent('点击文章卡片', {
        content_title: articleTitle,
      });
    },
    filterCategory: (category: string) => {
      trackEvent('筛选文章分类', {
        filter_category: category,
      });
    },
  },

  // Banner相关
  banner: {
    click: (bannerTitle: string, bannerLink: string) => {
      trackEvent('点击Banner', {
        banner_title: bannerTitle,
        banner_link: bannerLink,
      });
    },
  },

  // 导航相关
  navigation: {
    clickMenu: (menuLabel: string, destination: string) => {
      trackEvent('点击导航菜单', {
        menu_label: menuLabel,
        destination: destination,
      });
    },
    clickLogo: () => {
      trackEvent('点击Logo', {
        destination: '首页',
      });
    },
  },

  // CTA按钮
  cta: {
    click: (buttonText: string, location: string) => {
      trackEvent('点击CTA按钮', {
        button_text: buttonText,
        button_location: location,
      });
    },
  },

  // 页面滚动
  scroll: {
    depth: (percentage: number, pagePath: string) => {
      trackEvent('页面滚动', {
        scroll_depth: `${percentage}%`,
        page_path: pagePath,
      });
    },
  },
};

export default analytics;
